import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CoreFrontendNotificationDto, PermissionNameType, SimpleUserInfoResponse } from "@mm-products/java-multima-framework";
import { TaxAccountantDto, TaxClientDto, TaxDocumentDto } from "../backend/taxguru";
import { SUPER_ADMIN_ROLE } from "../permissions/usePermissions";

export type UserState = {
    userId: string | undefined;
    username: string | undefined;
    permissions: Array<PermissionNameType> | undefined;
    accountant: TaxAccountantDto | undefined;
    changedDocument: TaxDocumentDto | undefined;
    credit: { oldCredit: number | undefined; credit: number } | undefined;
    client: TaxClientDto | undefined;
    notifications: Array<CoreFrontendNotificationDto>;
    isAdmin: boolean | undefined;
};

const initialState: UserState = {
    isAdmin: undefined,
    permissions: undefined,
    userId: undefined,
    username: undefined,
    credit: undefined,
    changedDocument: undefined,
    accountant: undefined,
    client: undefined,
    notifications: [],
};

const sortNotificationsByDate = (notifications: Array<CoreFrontendNotificationDto>) => {
    return notifications.sort((a, b) => {
        if (!a.created) {
            return 1;
        } else if (!b.created) {
            return -1;
        } else {
            return new Date(b.created).getTime() - new Date(a.created).getTime();
        }
    });
};

export const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setUserInfo: (state, action: PayloadAction<SimpleUserInfoResponse>) => {
            const { username, id, permissions } = action.payload;
            if (!username || !id) {
                throw new Error("setUserInfo does not accept undefined values");
            }
            state.username = username;
            state.userId = id;
            state.permissions = permissions ?? [];
            state.isAdmin = (permissions ?? []).some((per) => per === SUPER_ADMIN_ROLE);
        },
        setCredit: (state, action) => {
            if (action.payload) {
                // is accountant
                state.credit = {
                    oldCredit: state?.credit?.credit,
                    credit: action.payload,
                };
            }
        },
        setChangedDocument: (state, action) => {
            state.changedDocument = action.payload;
        },
        setUserAccountant: (state, action: PayloadAction<TaxAccountantDto>) => {
            state.accountant = action.payload;
        },
        setUserClient: (state, action: PayloadAction<TaxClientDto>) => {
            state.client = action.payload;
        },
        resetUserSlice: () => {
            return initialState;
        },
        addNotification: (state, action) => {
            if (Array.isArray(action.payload)) {
                state.notifications.push(...action.payload);
            } else {
                state.notifications.push(action.payload);
            }
            state.notifications = sortNotificationsByDate(state.notifications);
        },
        resetNotifications: (state) => {
            state.notifications = initialState.notifications;
        },
    },
});

// Action creators are generated for each case reducer function
export const { setUserClient, setUserAccountant, setUserInfo, setCredit, resetUserSlice, addNotification, resetNotifications, setChangedDocument } =
    userSlice.actions;

export default userSlice.reducer;
